import React from 'react';
import { DEFAULT_RISK_BANDS, resolveBand } from '../utils/riskBands';

// Cell backgrounds are a lightened tint of the band color so the I/R
// badges stay readable (band hex + alpha).
const CELL_BG_ALPHA = '59'; // ~35% opacity

/**
 * RiskMatrix5x5
 *
 * Props:
 *   initialSeverity    number 1-5  (required)
 *   initialLikelihood  number 1-5  (required)
 *   residualSeverity   number 1-5 | null
 *   residualLikelihood number 1-5 | null
 *   showLegend         bool (default true)
 *   compact            bool — smaller cells for use inside hazard cards (default false)
 *   bands              tenant risk bands [{min,max,label,color,acceptable}]
 *                      (defaults to the product band set)
 */
const RiskMatrix5x5 = ({
  initialSeverity,
  initialLikelihood,
  residualSeverity = null,
  residualLikelihood = null,
  showLegend = true,
  compact = false,
  bands = DEFAULT_RISK_BANDS,
}) => {
  const hasResidual = residualSeverity && residualLikelihood;

  const isInitial  = (s, l) => s === initialSeverity  && l === initialLikelihood;
  const isResidual = (s, l) => hasResidual && s === residualSeverity && l === residualLikelihood;

  const initialScore  = initialSeverity * initialLikelihood;
  const residualScore = hasResidual ? residualSeverity * residualLikelihood : null;
  const riskReduced   = hasResidual && residualScore < initialScore;
  const riskUnchanged = hasResidual && residualScore === initialScore;

  const cellSize  = compact ? 'h-7'  : 'h-9';
  const badgeSize = compact ? 'w-4 h-4 text-[9px]' : 'w-5 h-5 text-[10px]';
  const scoreText = compact ? 'text-[9px]' : 'text-xs';

  return (
    <div className="w-full select-none">
      <div className="flex gap-1">
        {/* Severity Y-axis label */}
        <div className="flex items-center justify-center" style={{ width: 14 }}>
          <span
            className="text-[9px] text-gray-400 font-semibold"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: 1 }}
          >
            SEVERITY
          </span>
        </div>

        <div className="flex-1 min-w-0">
          {/* Column header: Likelihood numbers */}
          <div className="flex mb-0.5">
            <div style={{ width: 18 }} />
            {[1, 2, 3, 4, 5].map(l => (
              <div key={l} className="flex-1 text-center text-[10px] font-bold text-gray-500">{l}</div>
            ))}
          </div>

          {/* Rows: severity 5 (top) → 1 (bottom) */}
          {[5, 4, 3, 2, 1].map(s => (
            <div key={s} className="flex mb-0.5">
              <div style={{ width: 18 }} className="flex items-center justify-center text-[10px] font-bold text-gray-500 shrink-0">
                {s}
              </div>
              {[1, 2, 3, 4, 5].map(l => {
                const band    = resolveBand(s * l, bands);
                const showI   = isInitial(s, l);
                const showR   = isResidual(s, l);
                const showBoth = showI && showR;

                return (
                  <div
                    key={l}
                    className={`flex-1 mx-0.5 rounded flex items-center justify-center ${cellSize}`}
                    style={{ backgroundColor: `${band.color}${CELL_BG_ALPHA}` }}
                  >
                    {showBoth ? (
                      <div className="flex gap-0.5 items-center">
                        <span className={`${badgeSize} bg-gray-800 text-white rounded-full flex items-center justify-center font-black leading-none`}>I</span>
                        <span className={`${badgeSize} bg-blue-600 text-white rounded-full flex items-center justify-center font-black leading-none`}>R</span>
                      </div>
                    ) : showI ? (
                      <span className={`${badgeSize} bg-gray-800 text-white rounded-full flex items-center justify-center font-black leading-none`}>I</span>
                    ) : showR ? (
                      <span className={`${badgeSize} bg-blue-600 text-white rounded-full flex items-center justify-center font-black leading-none`}>R</span>
                    ) : (
                      <span className={`${scoreText} text-gray-500 opacity-60 font-medium`}>{s * l}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          {/* Likelihood X-axis label */}
          <div className="text-center text-[9px] text-gray-400 font-semibold mt-0.5" style={{ letterSpacing: 1 }}>
            LIKELIHOOD
          </div>
        </div>
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 justify-center">
          <div className="flex items-center gap-1">
            <span className="w-4 h-4 bg-gray-800 text-white rounded-full text-[9px] flex items-center justify-center font-black">I</span>
            <span className="text-xs text-gray-600">Initial</span>
          </div>
          {hasResidual && (
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 bg-blue-600 text-white rounded-full text-[9px] flex items-center justify-center font-black">R</span>
              <span className="text-xs text-gray-600">Residual</span>
            </div>
          )}
          {riskReduced && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-green-700 font-semibold">↓ Risk reduced</span>
            </div>
          )}
          {riskUnchanged && hasResidual && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-yellow-700 font-semibold">→ Unchanged</span>
            </div>
          )}
          {(bands && bands.length ? bands : DEFAULT_RISK_BANDS).map(b => (
            <div key={b.label} className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-sm border border-gray-300" style={{ backgroundColor: `${b.color}${CELL_BG_ALPHA}` }} />
              <span className="text-xs text-gray-500">{b.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RiskMatrix5x5;
